<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ScheduledMessageItem from 'dashboard/components-next/ScheduledMessages/ScheduledMessageItem.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();

const pending = useMapGetter('scheduledMessages/getPending');
const sent = useMapGetter('scheduledMessages/getSent');
const uiFlags = useMapGetter('scheduledMessages/getUIFlags');

const editDialogRef = ref(null);
const editing = ref(null);
const content = ref('');
const scheduledAt = ref('');

watch(
  () => props.conversationId,
  id => {
    if (id) store.dispatch('scheduledMessages/get', id);
  },
  { immediate: true }
);

// The datetime-local input speaks local time without a zone, so the epoch is converted here and
// back on save.
const toLocalInput = epochSeconds => {
  const date = new Date(epochSeconds * 1000);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const onEdit = scheduledMessage => {
  editing.value = scheduledMessage;
  content.value = scheduledMessage.content;
  scheduledAt.value = toLocalInput(scheduledMessage.scheduled_at);
  editDialogRef.value.open();
};

const onSave = async () => {
  try {
    await store.dispatch('scheduledMessages/update', {
      conversationId: props.conversationId,
      id: editing.value.id,
      content: content.value,
      scheduled_at: new Date(scheduledAt.value).toISOString(),
    });
    editDialogRef.value.close();
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGES.API.ERROR'));
  }
};

const onCancel = async scheduledMessage => {
  try {
    await store.dispatch('scheduledMessages/cancel', {
      conversationId: props.conversationId,
      id: scheduledMessage.id,
    });
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGES.API.ERROR'));
  }
};

const onSendNow = async scheduledMessage => {
  try {
    await store.dispatch('scheduledMessages/sendNow', {
      conversationId: props.conversationId,
      id: scheduledMessage.id,
    });
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGES.API.ERROR'));
  }
};

const isEmpty = computed(
  () => !uiFlags.value.isFetching && !pending.value.length && !sent.value.length
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <p v-if="isEmpty" class="mb-0 text-sm text-n-slate-11">
      {{ t('CONVERSATION.SCHEDULED_MESSAGES.EMPTY') }}
    </p>

    <div v-if="pending.length" class="flex flex-col gap-2">
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('CONVERSATION.SCHEDULED_MESSAGES.PENDING_TITLE') }}
      </h4>
      <ScheduledMessageItem
        v-for="scheduledMessage in pending"
        :key="scheduledMessage.id"
        :scheduled-message="scheduledMessage"
        @edit="onEdit"
        @cancel="onCancel"
        @send-now="onSendNow"
      />
    </div>

    <div v-if="sent.length" class="flex flex-col gap-2">
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('CONVERSATION.SCHEDULED_MESSAGES.SENT_TITLE') }}
      </h4>
      <ScheduledMessageItem
        v-for="scheduledMessage in sent"
        :key="scheduledMessage.id"
        :scheduled-message="scheduledMessage"
      />
    </div>

    <Dialog
      ref="editDialogRef"
      :title="t('CONVERSATION.SCHEDULED_MESSAGES.EDIT_TITLE')"
      :confirm-button-label="t('CONVERSATION.SCHEDULED_MESSAGES.SAVE')"
      :is-loading="uiFlags.isUpdating"
      @confirm="onSave"
    >
      <div class="flex flex-col gap-3">
        <TextArea v-model="content" auto-height />
        <Input
          v-model="scheduledAt"
          type="datetime-local"
          :label="t('CONVERSATION.SCHEDULED_MESSAGES.WHEN')"
        />
      </div>
    </Dialog>
  </div>
</template>
